/**
 * ColumnSelector Component
 * Allows users to show/hide table columns
 * Persists preferences to localStorage
 */

import React, { useState, useRef, useEffect } from 'react';
import './ColumnSelector.css';

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  required?: boolean; // Cannot be hidden
}

interface ColumnSelectorProps {
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
  storageKey?: string;
}

export const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  onColumnsChange,
  storageKey = 'table-columns'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleColumn = (key: string) => {
    const updatedColumns = columns.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    );
    onColumnsChange(updatedColumns);
    
    // Persist to localStorage
    if (storageKey) {
      const visibilityMap = updatedColumns.reduce((acc, col) => {
        acc[col.key] = col.visible;
        return acc;
      }, {} as Record<string, boolean>);
      localStorage.setItem(storageKey, JSON.stringify(visibilityMap));
    }
  };

  const visibleCount = columns.filter(col => col.visible).length;

  return (
    <div className="column-selector" ref={dropdownRef}>
      <button
        className="column-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Configure columns"
      >
        <span className="column-selector-icon">⚙️</span>
        <span className="column-selector-label">Columns</span>
        <span className="column-selector-count">({visibleCount})</span>
        <span className="column-selector-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="column-selector-dropdown">
          <div className="column-selector-header">
            <span>Show/Hide Columns</span>
          </div>
          <div className="column-selector-list">
            {columns.map(column => (
              <label
                key={column.key}
                className={`column-selector-item ${column.required ? 'required' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={column.visible}
                  onChange={() => handleToggleColumn(column.key)}
                  disabled={column.required}
                />
                <span className="column-selector-checkbox">
                  {column.visible ? '☑' : '☐'}
                </span>
                <span className="column-selector-item-label">{column.label}</span>
                {column.required && (
                  <span className="column-selector-required-badge">Required</span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
